"use client";

import { useState } from "react";
import { Globe } from "lucide-react";
import { connectorIconDarkPath, connectorIconPath } from "@/lib/connector-icons";

/**
 * A connector's mark, served from our own origin. Callers pass the connector
 * id, never a slug or a URL, so where icons come from stays one file's problem.
 *
 * Nothing is fetched from a third party at render time: icons used to come off
 * the Simple Icons CDN, which meant every card leaked the page's connectors to
 * another host, went blank offline, and changed under us whenever a brand was
 * pulled from that set.
 *
 * A mark too dark to see on the dark surface ships a lighter twin, and CSS
 * picks between them. next-themes sets `.dark` before first paint, but its
 * resolved theme is not readable on the first render, so choosing in JS would
 * flash the wrong mark on every load.
 */
export function BrandIcon({
  id,
  size = 18,
  alt = "",
  className,
}: {
  /** Connector id, e.g. "figma", "google-drive". */
  id: string;
  size?: number;
  alt?: string;
  className?: string;
}) {
  const src = connectorIconPath(id);
  const darkSrc = connectorIconDarkPath(id);
  const [broken, setBroken] = useState(false);

  if (!src || broken) return <Globe size={size} className={className} />;

  const mark = (source: string, cls: string | undefined) => (
    <img
      src={source}
      alt={alt}
      width={size}
      height={size}
      className={cls}
      style={{ width: size, height: size, objectFit: "contain" }}
      onError={() => setBroken(true)}
    />
  );

  // Without a twin the output stays a bare <img>, so no existing layout moves.
  if (!darkSrc) return mark(src, className);

  return (
    <span className={className}>
      {mark(src, "dark:hidden")}
      {mark(darkSrc, "hidden dark:block")}
    </span>
  );
}

export default BrandIcon;
